import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Lock, Info, MapPin, AlertCircle, CheckCircle, Upload, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { isValidZipCode, BUSINESS_NEXUS } from '../utils/salesTax';
import { ordersAPI } from '../utils/api';
import ShippingRateSelector from '../components/ShippingRateSelector';
import ArtworkUpload from '../components/ArtworkUpload';

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const CheckoutPage = () => {
  const { 
    cartItems, 
    cartTotal, 
    clearCart, 
    shippingCost, 
    hasFreeShipping,
    updateShippingZip,
    taxCalculation,
    taxAmount,
    orderTotal,
  } = useCart();
  
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [processing, setProcessing] = useState(false);
  const [zipError, setZipError] = useState('');
  const [orderError, setOrderError] = useState('');
  const [orderComplete, setOrderComplete] = useState(false);
  const [selectedShippingRate, setSelectedShippingRate] = useState(null);
  const [artworkFiles, setArtworkFiles] = useState([]);
  const [artworkSessionId, setArtworkSessionId] = useState(null);
  const [paymentOption, setPaymentOption] = useState('pay_now'); // 'pay_now' or 'pay_later'
  
  // Check if user can use Pay Later option
  const canPayLater = isAuthenticated && user?.allow_pay_later === true;
  
  const [formData, setFormData] = useState({
    // Billing Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    // Shipping Address
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    // Payment
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  });

  // Update tax calculation when ZIP changes
  useEffect(() => {
    if (formData.zip && formData.zip.length === 5) {
      if (isValidZipCode(formData.zip)) {
        updateShippingZip(formData.zip);
        setZipError('');
      } else {
        setZipError('Please enter a valid 5-digit ZIP code');
      }
    } else if (formData.zip.length > 0 && formData.zip.length < 5) {
      setZipError('');
    }
  }, [formData.zip, updateShippingZip]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format ZIP code - only allow digits
    if (name === 'zip') {
      const cleanZip = value.replace(/\D/g, '').slice(0, 5);
      setFormData(prev => ({ ...prev, [name]: cleanZip }));
      return;
    }
    
    // Format card number
    if (name === 'cardNumber') {
      const cleanNumber = value.replace(/\D/g, '').slice(0, 16);
      const formatted = cleanNumber.replace(/(\d{4})/g, '$1 ').trim();
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }
    
    // Format expiry date
    if (name === 'cardExpiry') {
      const cleanExpiry = value.replace(/\D/g, '').slice(0, 4);
      const formatted = cleanExpiry.length > 2 
        ? `${cleanExpiry.slice(0, 2)}/${cleanExpiry.slice(2)}` 
        : cleanExpiry;
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }
    
    // Format CVC
    if (name === 'cardCvc') {
      const cleanCvc = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, [name]: cleanCvc }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleStateChange = (value) => {
    setFormData(prev => ({ ...prev, state: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOrderError('');
    
    // Validate ZIP
    if (!isValidZipCode(formData.zip)) {
      setZipError('Please enter a valid ZIP code');
      return;
    }
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.address || !formData.city || !formData.state) {
      setOrderError('Please fill in all required fields');
      return;
    }
    
    // Validate shipping method selected
    if (!selectedShippingRate) {
      setOrderError('Please select a shipping method');
      return;
    }
    
    setProcessing(true);

    try {
      // Calculate final total with selected shipping
      const finalShipping = selectedShippingRate.rate;
      const finalTotal = cartTotal + finalShipping + taxAmount;
      
      // Prepare order data for API
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.productId || item.id,
          product_name: item.name,
          section: item.section || 'small-format',
          quantity: item.quantity,
          unit_price: item.basePrice,
          options: item.selectedOptions || {},
          image: item.image,
          sku: item.sku || null
        })),
        shipping_address: {
          full_name: `${formData.firstName} ${formData.lastName}`,
          address_line1: formData.address,
          address_line2: formData.company || null,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip,
          country: formData.country,
          phone: formData.phone
        },
        shipping_method: {
          carrier: selectedShippingRate.carrier,
          service_name: selectedShippingRate.service_name,
          service_code: selectedShippingRate.service_code,
          rate: selectedShippingRate.rate,
          delivery_estimate: selectedShippingRate.delivery_estimate,
          transit_days: selectedShippingRate.transit_days
        },
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        // Include user_id if logged in (for order history)
        user_id: isAuthenticated && user ? user.id : null,
        // Artwork files
        artwork_files: artworkFiles.length > 0 ? artworkFiles : null,
        artwork_session_id: artworkSessionId,
        notes: null,
        // Order source - web checkout
        order_source: 'web',
        // Pay Later option
        is_pay_later: paymentOption === 'pay_later' && canPayLater
      };

      // Call orders API
      const response = await ordersAPI.create(orderData);
      
      // Store order details for confirmation page
      sessionStorage.setItem('lastOrder', JSON.stringify({
        order_id: response.order_id,
        total: response.total,
        email: formData.email,
        items: response.items
      }));
      
      // Mark order as complete BEFORE clearing cart to avoid redirect
      setOrderComplete(true);
      clearCart();
      
      toast({
        title: "Order placed successfully!",
        description: `Order #${response.order_id} confirmed. You'll receive a confirmation email shortly.`,
        duration: 5000,
      });
      
      navigate('/order-confirmation', { 
        state: { 
          orderId: response.order_id,
          email: formData.email 
        }
      });
      
    } catch (error) {
      console.error('Order creation failed:', error);
      setOrderError(error.message || 'Failed to place order. Please try again.');
      toast({
        title: "Order failed",
        description: error.message || 'Failed to place order. Please try again.',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  if (cartItems.length === 0 && !orderComplete) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Header - Luxury styled */}
      <div className="relative gradient-hero py-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link
            to="/cart"
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-4 transition-colors font-body"
            data-testid="back-to-cart"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cart
          </Link>
          <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">Checkout</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Order Error Message */}
        {orderError && (
          <div className="max-w-7xl mx-auto mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-red-600 font-body flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {orderError}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Information */}
              <Card className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardHeader>
                  <CardTitle className="font-heading text-ivory-800">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-ivory-600 font-body">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-ivory-600 font-body">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-ivory-600 font-body">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-ivory-600 font-body">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-ivory-600 font-body">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Shipping Address */}
              <Card className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardHeader>
                  <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gold-600" />
                    Shipping Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="address" className="text-ivory-600 font-body">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-ivory-600 font-body">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-ivory-600 font-body">State *</Label>
                      <Select value={formData.state} onValueChange={handleStateChange}>
                        <SelectTrigger className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body" data-testid="state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="bg-ivory-50 border-ivory-300 max-h-60">
                          {US_STATES.map(state => (
                            <SelectItem 
                              key={state.code} 
                              value={state.code}
                              className="text-ivory-700 hover:bg-gold-500/10 font-body"
                            >
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip" className="text-ivory-600 font-body">ZIP Code *</Label>
                      <Input
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        required
                        maxLength={5}
                        placeholder="92705"
                        className={`mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body ${zipError ? 'border-red-500' : ''}`}
                        data-testid="zip-input"
                      />
                      {zipError && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1 font-body">
                          <AlertCircle className="w-3 h-3" />
                          {zipError}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="country" className="text-ivory-600 font-body">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        disabled
                        className="mt-1 bg-ivory-200 border-ivory-300 text-ivory-600 font-body"
                      />
                    </div>
                  </div>
                  
                  {/* Tax Info Banner */}
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-ivory-200/50 border border-ivory-300">
                    <Info className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-ivory-600 font-body">
                      <p className="font-medium text-ivory-700">Sales tax calculated based on shipping ZIP code.</p>
                      <p className="mt-1">
                        Our business location: {BUSINESS_NEXUS.city}, {BUSINESS_NEXUS.state} {BUSINESS_NEXUS.zip}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Artwork Upload */}
              <Card className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardHeader>
                  <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-gold-600" />
                    Upload Artwork
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ArtworkUpload
                    uploadedFiles={artworkFiles}
                    onFilesUploaded={setArtworkFiles}
                    sessionId={artworkSessionId}
                    onSessionIdChange={setArtworkSessionId}
                    disabled={processing}
                  />
                  
                  {/* Production Notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-amber-700 text-sm font-body">
                      <strong>Production Timeline:</strong> 5 business days after artwork approval + shipping transit time.
                    </p>
                    <p className="text-amber-600 text-sm font-body mt-2">
                      <strong>Need it sooner?</strong> Rush production may be available. Contact{' '}
                      <a href="mailto:sales@engraveca.com" className="font-semibold text-gold-700 hover:underline">
                        sales@engraveca.com
                      </a>
                      {' '}before placing your order.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Information */}
              <Card className="bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardHeader>
                  <CardTitle className="font-heading text-ivory-800 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gold-600" />
                    Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Payment Option Selection - Show if user can pay later */}
                  {canPayLater && (
                    <div className="space-y-3 pb-4 border-b border-ivory-200">
                      <Label className="text-ivory-600 font-body">Payment Option</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPaymentOption('pay_now')}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            paymentOption === 'pay_now' 
                              ? 'border-gold-500 bg-gold-500/10' 
                              : 'border-ivory-300 hover:border-gold-300'
                          }`}
                          data-testid="pay-now-option"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="w-4 h-4 text-gold-600" />
                            <span className="font-medium text-ivory-800 font-body">Pay Now</span>
                          </div>
                          <p className="text-xs text-ivory-500 font-body">
                            Pay with credit card
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentOption('pay_later')}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            paymentOption === 'pay_later' 
                              ? 'border-gold-500 bg-gold-500/10' 
                              : 'border-ivory-300 hover:border-gold-300'
                          }`}
                          data-testid="pay-later-option"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-gold-600" />
                            <span className="font-medium text-ivory-800 font-body">Pay Later</span>
                          </div>
                          <p className="text-xs text-ivory-500 font-body">
                            Invoice after artwork approval
                          </p>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Credit Card Fields - Only show if Pay Now selected */}
                  {paymentOption === 'pay_now' && (
                    <>
                      <div>
                        <Label htmlFor="cardNumber" className="text-ivory-600 font-body">Card Number *</Label>
                        <Input
                          id="cardNumber"
                          name="cardNumber"
                          placeholder="1234 5678 9012 3456"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          required={paymentOption === 'pay_now'}
                          className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body font-mono"
                          data-testid="card-number-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="cardExpiry" className="text-ivory-600 font-body">Expiry Date *</Label>
                          <Input
                            id="cardExpiry"
                            name="cardExpiry"
                            placeholder="MM/YY"
                            value={formData.cardExpiry}
                            onChange={handleInputChange}
                            required={paymentOption === 'pay_now'}
                            className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body font-mono"
                            data-testid="card-expiry-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cardCvc" className="text-ivory-600 font-body">CVC *</Label>
                          <Input
                            id="cardCvc"
                            name="cardCvc"
                            placeholder="123"
                            value={formData.cardCvc}
                            onChange={handleInputChange}
                            required={paymentOption === 'pay_now'}
                            className="mt-1 bg-ivory-50 border-ivory-300 text-ivory-800 font-body font-mono"
                            data-testid="card-cvc-input"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ivory-500 bg-ivory-50 p-3 rounded-lg border border-ivory-200">
                        <Lock className="w-4 h-4 text-gold-600" />
                        <span className="font-body">Your payment information is secure and encrypted</span>
                      </div>
                    </>
                  )}
                  
                  {/* Pay Later Info */}
                  {paymentOption === 'pay_later' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 font-body">Pay Later Selected</p>
                          <p className="text-sm text-amber-700 font-body mt-1">
                            Your order will be placed without payment. You can upload artwork from your order page. 
                            We'll send an invoice for payment after artwork is approved.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 bg-ivory-100 border border-ivory-300/50 shadow-luxury">
                <CardHeader className="border-b border-ivory-200">
                  <CardTitle className="font-heading text-ivory-800">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Cart Items */}
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex gap-3 pb-3 border-b border-ivory-200">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm text-ivory-800 line-clamp-2 font-body">{item.name}</h4>
                          <p className="text-xs text-ivory-500 mt-1 font-body">Qty: {item.quantity}</p>
                          <p className="text-sm font-semibold text-gold-600 mt-1 font-body">${item.total.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-3 pt-4 border-t border-ivory-200 font-body">
                    <div className="flex justify-between text-ivory-600">
                      <span>Subtotal</span>
                      <span className="font-semibold text-ivory-800">${cartTotal.toFixed(2)}</span>
                    </div>
                    
                    {/* Shipping Rate Selector */}
                    <div className="py-3 border-y border-ivory-200">
                      <ShippingRateSelector
                        destinationZip={formData.zip}
                        subtotal={cartTotal}
                        itemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                        selectedRate={selectedShippingRate}
                        onRateSelect={setSelectedShippingRate}
                      />
                    </div>
                    
                    {/* Selected Shipping Summary */}
                    {selectedShippingRate && (
                      <div className="flex justify-between text-ivory-600">
                        <span>Shipping ({selectedShippingRate.service_name})</span>
                        <span className="font-semibold text-ivory-800">${selectedShippingRate.rate.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Tax Line */}
                    <div className="flex justify-between text-ivory-600">
                      <div className="flex items-center gap-1">
                        <span>{taxCalculation.taxDescription || 'Sales Tax'}</span>
                        {taxCalculation.taxRateDisplay && taxCalculation.taxRate > 0 && (
                          <span className="text-xs text-ivory-400">({taxCalculation.taxRateDisplay})</span>
                        )}
                      </div>
                      <span className="font-semibold text-ivory-800" data-testid="tax-amount">
                        ${taxAmount.toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Tax Info */}
                    {taxCalculation.isCaliforniaNexus && taxCalculation.location && (
                      <div className="bg-gold-500/10 border border-gold-500/20 rounded-lg p-2">
                        <p className="text-xs text-gold-700 font-body">
                          Tax calculated for {taxCalculation.location.city}, {taxCalculation.location.county} County
                        </p>
                      </div>
                    )}
                    
                    {taxCalculation.isOutOfState && (
                      <div className="bg-ivory-200 border border-ivory-300 rounded-lg p-2">
                        <p className="text-xs text-ivory-600 font-body">
                          No sales tax for out-of-state orders
                        </p>
                      </div>
                    )}
                    
                    {!formData.zip && (
                      <div className="bg-ivory-200 border border-ivory-300 rounded-lg p-2">
                        <p className="text-xs text-ivory-500 font-body flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          Enter ZIP code to calculate tax & shipping
                        </p>
                      </div>
                    )}
                    
                    <div className="border-t border-ivory-200 pt-3">
                      <div className="flex justify-between text-lg">
                        <span className="font-semibold text-ivory-800">Total</span>
                        <span className="font-bold text-gold-600 text-2xl" data-testid="order-total">
                          ${(cartTotal + (selectedShippingRate?.rate || 0) + taxAmount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full text-lg py-6 btn-luxury-primary rounded-lg font-body"
                    disabled={processing || !formData.zip}
                    data-testid="place-order-btn"
                  >
                    {processing ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : paymentOption === 'pay_later' ? (
                      <span className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Place Order (Pay Later)
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Place Order & Pay
                      </span>
                    )}
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="outline"
                    className="w-full btn-luxury-outline rounded-lg font-body"
                    asChild
                  >
                    <Link to="/cart">Edit Cart</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
